<template>
  <div class="floating-chat" :class="{ 'floating-chat--open': isOpen }" :style="isOpen ? {} : dragStyle">
    <!-- Chat Button (when closed) -->
    <v-btn
      v-if="!isOpen"
      fab
      dark
      large
      color="#F4811F"
      class="floating-chat__btn"
      elevation="6"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="toggleChat"
    >
      <v-badge
        :content="totalUnread > 0 ? (totalUnread > 99 ? '99+' : totalUnread) : '0'"
        :value="totalUnread > 0"
        color="error"
        overlap
      >
        <v-icon>mdi-chat-processing-outline</v-icon>
      </v-badge>
    </v-btn>

    <!-- Chat Panel (when open) -->
    <v-card v-else class="floating-chat__panel" elevation="6">
      <!-- Header -->
      <div class="floating-chat__header bg-primary">
        <div class="d-flex align-center w-100 floating-chat__header-inner">
          <v-btn v-if="activeConversationId" icon dark small @click="backToList" class="mr-2">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="white--text font-weight-bold grow">
            {{ activeConversationId ? activePartnerName : 'ข้อความของฉัน' }}
          </span>
          <v-btn icon dark small @click="toggleChat" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Body: Conversation List OR Chat Window -->
      <div class="floating-chat__body">
        <template v-if="!activeConversationId">
          <!-- Loading State -->
          <div v-if="loading" class="floating-chat__loading">
            <v-progress-circular indeterminate color="#F4811F" />
          </div>
          
          <!-- Conversation List -->
          <ChatConversationList
            v-else
            :conversations="conversations"
            view-role="user"
            @select="openConversation"
          />
        </template>
        
        <template v-else>
          <!-- Chat Window -->
          <ChatWindow
            :conversation-id="activeConversationId"
            :partner-name="activePartnerName"
            :partner-avatar="activePartnerAvatar"
            :partner-online="activePartnerOnline"
            my-role="user"
            :show-back="false"
          />
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
import ChatConversationList from "@/components/Chat/ChatConversationList.vue";
import ChatWindow from "@/components/Chat/ChatWindow.vue";
import { getConversationsApi, createConversationApi } from "@/services/api/chat";

export default {
  name: "UserFloatingChat",
  components: {
    ChatConversationList,
    ChatWindow,
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      conversations: [],
      activeConversationId: null,
      activePartnerName: "",
      activePartnerAvatar: "",
      activePartnerOnline: false,
      totalUnread: 0,
      
      // Dragging state
      isDragging: false,
      hasDragged: false,
      dragStartPoint: { x: 0, y: 0 },
      startPos: { x: 0, y: 0 },
      currentPos: { x: 0, y: 0 },
      dragStyle: {},
    };
  },
  created() {
    this.$root.$on("open-floating-chat", this.handleExternalOpen);
  },
  mounted() {
    if (this.$store.getters.isAuthenticated) {
      this.loadConversations();
      this.attachSocketWhenReady();
    }
  },
  beforeDestroy() {
    this.$root.$off("open-floating-chat", this.handleExternalOpen);
    this.cleanupSocket();
    if (this._socketRetryId) clearInterval(this._socketRetryId);
  },
  methods: {
    startDrag(e) {
      if (this.isOpen) return;
      this.hasDragged = false;
      const touch = e.type.includes('touch') ? e.touches[0] : e;
      this.dragStartPoint = { x: touch.clientX, y: touch.clientY };
      this.startPos = {
        x: touch.clientX - (this.currentPos.x || 0),
        y: touch.clientY - (this.currentPos.y || 0)
      };
      
      document.addEventListener('mousemove', this.doDrag, { passive: false });
      document.addEventListener('touchmove', this.doDrag, { passive: false });
      document.addEventListener('mouseup', this.endDrag);
      document.addEventListener('touchend', this.endDrag);
    },
    doDrag(e) {
      if (this.isOpen) return;
      const touch = e.type.includes('touch') ? e.touches[0] : e;
      
      const dx = touch.clientX - this.dragStartPoint.x;
      const dy = touch.clientY - this.dragStartPoint.y;
      
      if (!this.hasDragged && Math.sqrt(dx*dx + dy*dy) > 5) {
        this.hasDragged = true;
      }
      
      if (!this.hasDragged) return;
      e.preventDefault();
      
      const nx = touch.clientX - this.startPos.x;
      const ny = touch.clientY - this.startPos.y;
      this.currentPos = { x: nx, y: ny };
      this.dragStyle = {
        transform: `translate(${nx}px, ${ny}px)`,
        transition: 'none'
      };
    },
    endDrag() {
      document.removeEventListener('mousemove', this.doDrag);
      document.removeEventListener('touchmove', this.doDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('touchend', this.endDrag);
    },
    resetDragPosition() {
      this.currentPos = { x: 0, y: 0 };
      this.dragStyle = {};
    },

    async toggleChat(e) {
      if (this.hasDragged) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      if (!this.isOpen && !this.$store.getters.isAuthenticated) {
        this.$swal.fire("กรุณาเข้าสู่ระบบ", "ต้องเข้าสู่ระบบเพื่อใช้งานแชท", "warning");
        return;
      }

      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.resetDragPosition();
        await this.loadConversations();
      }
    },
    async loadConversations() {
      if(!this.$store.getters.isAuthenticated) return;
      
      this.loading = true;
      try {
        const data = await getConversationsApi("user");
        this.conversations = data;
        this.calculateUnread();
      } catch (err) {
        console.error("loadConversations error:", err);
      } finally {
        this.loading = false;
      }
    },
    calculateUnread() {
      this.totalUnread = this.conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
    },
    openConversation(conv) {
      this.activeConversationId = conv.id;
      // detect rider or seller conversation
      if (conv.rider) {
        this.activePartnerName = [conv.rider.natId_firstName, conv.rider.natId_lastName].filter(Boolean).join(" ") || "ไรเดอร์";
        const img = conv.rider.profileImage;
        this.activePartnerAvatar = img ? (img.startsWith("http") ? img : `${this.$domain_api}${img}`) : "";
        this.activePartnerOnline = conv.rider.isOnline || false;
      } else {
        this.activePartnerName = conv.seller?.shopName || "ร้านค้า";
        const img = conv.seller?.storeImage;
        this.activePartnerAvatar = img ? (img.startsWith("http") ? img : `${this.$domain_api}${img}`) : "";
        this.activePartnerOnline = conv.seller?.isOnline || false;
      }

      // Clear unread for this conv locally
      if (conv.unreadCount > 0) {
        conv.unreadCount = 0;
        this.calculateUnread();
      }
    },
    backToList() {
      this.activeConversationId = null;
      this.activePartnerName = "";
      this.activePartnerAvatar = "";
      this.activePartnerOnline = false;
      this.loadConversations(); // refresh list to get latest previews
    },
    async handleExternalOpen(payload) {
       // payload can be a string (legacy: sellerId) or { sellerId } | { riderId }
       const token = localStorage.getItem("user_token") || localStorage.getItem("token");
       if (!token) {
           this.$swal.fire("กรุณาเข้าสู่ระบบ", "ต้องเข้าสู่ระบบเพื่อใช้งานแชท", "warning");
           return;
       }

       if (!this.isOpen) {
           this.isOpen = true;
           this.setupSocketListeners();
       }

       let sellerId = null, riderId = null;
       if (typeof payload === "string") sellerId = payload;
       else if (payload && typeof payload === "object") {
         sellerId = payload.sellerId || null;
         riderId = payload.riderId || null;
       }

       if (sellerId || riderId) {
           try {
             this.loading = true;
             const body = sellerId ? { sellerId } : { riderId };
             const data = await createConversationApi("user", body);
             this.openConversation(data);
           } catch (err) {
             console.error("Error starting chat via floating widget:", err);
             const msg = err?.response?.data?.message || "ไม่สามารถเริ่มการสนทนาได้";
             this.$swal.fire("ผิดพลาด", msg, "warning");
             this.isOpen = false;
           } finally {
               this.loading = false;
           }
       } else {
           await this.loadConversations();
           this.activeConversationId = null;
       }
    },
    
    // --- Realtime socket logic — always active while component is mounted ---
    attachSocketWhenReady() {
      if (this.setupSocketListeners()) return;
      // Socket not ready yet — poll until it is (max ~30s)
      let attempts = 0;
      this._socketRetryId = setInterval(() => {
        attempts++;
        if (this.setupSocketListeners() || attempts > 60) {
          clearInterval(this._socketRetryId);
          this._socketRetryId = null;
        }
      }, 500);
    },
    setupSocketListeners() {
      if (!this.$socket) return false;
      this.$socket.off("chat:updateConversationList", this.onUpdateConversationList);
      this.$socket.off("chat:notification", this.onGlobalNewMessage);
      this.$socket.on("chat:updateConversationList", this.onUpdateConversationList);
      this.$socket.on("chat:notification", this.onGlobalNewMessage);
      return true;
    },
    cleanupSocket() {
      if (!this.$socket) return;
      this.$socket.off("chat:updateConversationList", this.onUpdateConversationList);
      this.$socket.off("chat:notification", this.onGlobalNewMessage);
    },
    onUpdateConversationList() {
      this.loadConversations();
    },
    onGlobalNewMessage({ conversationId }) {
      // If the user is currently viewing this specific conversation inside the open chat,
      // the ChatWindow itself handles real-time append — we still refresh the list to
      // update last-message previews. For all other cases we just refresh the badge + list.
      this.loadConversations();
      if (this.activeConversationId !== conversationId) {
        this.$nextTick(() => {
          try { new Audio("data:audio/wav;base64,UklGRhwAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=").play().catch(() => { /* ignore autoplay block */ }); } catch (_) { /* ignore */ }
        });
      }
    },
  },
};
</script>

<style scoped>
.floating-chat {
  position: fixed;
  bottom: 80px; /* Above mobile bottom nav */
  right: 16px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

@media (min-width: 768px) {
  .floating-chat {
    bottom: 24px;
    right: 24px;
  }
}

.floating-chat__btn {
  cursor: grab;
  touch-action: none;
  transition: transform 0.2s, box-shadow 0.2s;
}

.floating-chat__btn:active {
  cursor: grabbing;
}

.floating-chat__panel {
  width: 380px;
  height: 600px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 10px 40px -10px rgba(0,0,0,0.3) !important;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@media (max-width: 768px) {
  .floating-chat--open {
    bottom: 0 !important;
    right: 0 !important;
    left: 0 !important;
    top: 0 !important;
    z-index: 9999;
    align-items: center;
    justify-content: flex-end;
  }

  .floating-chat--open .floating-chat__panel {
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0 !important;
    animation: slideUpMobile 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
}

@keyframes slideUpMobile {
  0% { transform: translateY(100%); }
  100% { transform: translateY(0); }
}

@keyframes slideUp {
  0% { opacity: 0; transform: translateY(20px) scale(0.95); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

.floating-chat__header {
  height: 56px;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 2;
}

.floating-chat__header-inner {
  padding: 12px 16px !important;
  position: relative;
}
.floating-chat__header .close-btn {
  position: absolute !important;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.bg-primary {
  background-color: #F4811F;
}

.floating-chat__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  position: relative;
}

.floating-chat__loading {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.8);
  z-index: 10;
}

/* Ensure ChatWindow inside fits perfectly */
.floating-chat__body >>> .chat-window {
  height: 100%;
}
</style>
